import React, { useState } from 'react';
import { AddOutlined, Error } from '@mui/icons-material';
import { IconButton, MenuItem, Popover } from '@mui/material';
import { CalendarType } from '@demind-inc/core';

import { CalendarOptionsType, calendars, SIDEBAR_ICONS } from '../../constants';
import './ManageCalendarsBtn.scss';
import { CustomTooltip } from '../../../common';
import { SyncErrorTooltipContent } from '../SyncErrorTooltipContent';
import { useTranslation } from 'react-i18next';

type ConnectCalendarAction = (calendarType: CalendarType) => Promise<boolean> | boolean | void;

interface ManageCalendarsBtnProps {
  onConnect: ConnectCalendarAction;
  syncErrorRootEmail?: string;
  syncErrorCalendarType?: CalendarType;
  disabledCalendarType?: CalendarType[];
}

interface ManageCalendarModalHeaderProps {
  onClose: () => void;
}

interface ManageCalendarModalContentProps {
  calendars: CalendarOptionsType[];
  disabledCalendarType: CalendarType[];
  onConnect: ConnectCalendarAction;
  onClose: () => void;
}

const ManageCalendarsBtn: React.FC<ManageCalendarsBtnProps> = ({
  disabledCalendarType = [],
  syncErrorCalendarType,
  syncErrorRootEmail,
  onConnect,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isAppleCalendarSupported = (window as any)?.lifestack?.platform === 'darwin';
  const availableCalendars = isAppleCalendarSupported
    ? calendars
    : calendars.filter(({ id }) => id !== 'apple');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const id = open ? 'manage-calendars' : undefined;

  return (
    <div className="manage-calendars-btn--container">
      <button
        aria-describedby={id}
        id="manage-calendars"
        className="manage-calendars-btn"
        onClick={handleClick}
      >
        <AddOutlined className="manage-calendars-btn__icon" />
        {t('calendar.sidebar.calendarAccounts')}
      </button>
      {!!syncErrorCalendarType && (
        <CustomTooltip
          title={
            <SyncErrorTooltipContent
              onReconnect={() => onConnect(syncErrorCalendarType)}
              email={syncErrorRootEmail}
            />
          }
        >
          <Error className="manage-calendars-btn__error" />
        </CustomTooltip>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        className="manage-calendars-btn__popover"
      >
        <div className="manage-calendars-btn__modal">
          <ModalHeader onClose={handleClose} />
          <ModalContent
            disabledCalendarType={disabledCalendarType}
            calendars={availableCalendars}
            onConnect={onConnect}
            onClose={handleClose}
          />
        </div>
      </Popover>
    </div>
  );
};

const ModalHeader: React.FC<ManageCalendarModalHeaderProps> = ({ onClose }) => (
  <ModalHeaderContent onClose={onClose} />
);

const ModalHeaderContent: React.FC<ManageCalendarModalHeaderProps> = ({ onClose }) => {
  const { t } = useTranslation();

  return (
    <div className="manage-calendars-btn__modal__header">
      <p>{t('calendar.sidebar.addCalendars')}</p>
      <IconButton onClick={onClose} className="manage-calendars-btn__modal__close-button">
        <img
          src={SIDEBAR_ICONS['close']}
          alt={t('calendar.common.close')}
          className="manage-calendars-btn__modal__close-button__icon"
        />
      </IconButton>
    </div>
  );
};

const ModalContent: React.FC<ManageCalendarModalContentProps> = ({
  calendars,
  onConnect,
  onClose,
}) => {
  const handleConnect = async (calendarType: CalendarType) => {
    const result = await onConnect(calendarType);

    if (calendarType === 'apple' && result === true) {
      onClose();
    }
  };

  return (
    <div className="manage-calendars-btn__modal__content">
      {calendars.map((calendarType) => (
        <MenuItem
          key={calendarType.id}
          className="manage-calendars-btn__modal__item"
          onClick={() => {
            void handleConnect(calendarType.id);
          }}
        >
          <div className="manage-calendars-btn__modal__item__text">
            <AddOutlined fontSize="small" className="manage-calendars-btn__modal__item__icon" />
            {calendarType.calendar}
          </div>
          {React.createElement(calendarType.icon, {
            // @ts-ignore
            className: 'manage-calendars-btn__modal__item__provider-icon',
          })}
        </MenuItem>
      ))}
    </div>
  );
};

export default ManageCalendarsBtn;
